import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, HelpCircle, Phone, MessageCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig } from '../config/business';

export default function FAQPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const { contact } = businessConfig;

  const faqs = [
    {
      question: 'What areas do you service?',
      answer: 'We provide moving services all across Melbourne and surrounding suburbs. Contact us to confirm if we cover your specific area.'
    },
    {
      question: 'How do I get a quote?',
      answer: 'You can get a free quote by clicking the "Get Your Free Quote" button on our homepage, calling us at 1800 233 023, or filling out our contact form.'
    },
    {
      question: 'Do you provide packing materials?',
      answer: 'Yes! We provide moving blankets, straps, and protective materials. We can also help with packing if needed.'
    },
    {
      question: 'Are you insured?',
      answer: 'Yes, Thrifty Move is fully insured and complies with all Australian regulations including HVNL and Chain of Responsibility requirements.'
    },
    {
      question: 'What truck size will I need?',
      answer: 'The right truck depends on the volume of your move. If you complete our inventory-based estimate, we\'ll use your item volume to help determine the appropriate capacity. If you\'re not sure, request a Quick Quote and our team will confirm the right setup with you.'
    },
    {
      question: 'Can you move on weekends?',
      answer: 'Yes, we operate 7 days a week. Contact us to check availability for your preferred date.'
    },
    {
      question: 'How do I make a complaint?',
      answer: 'We take complaints seriously. Please email us at info@thrifty-move.com.au. All complaints are acknowledged within 48 hours and investigated within 10 business days.'
    },
    {
      question: 'Do you move interstate?',
      answer: 'Currently, we focus on local moves within Melbourne and Victoria. Contact us to discuss your specific requirements.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept various payment methods including cash, bank transfer, and card payments. Payment details will be discussed during your quote.'
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking at least 1-2 weeks in advance, especially during peak moving season. However, we can often accommodate last-minute bookings.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      <Helmet>
        <title>Frequently Asked Questions | Melbourne Removalists | Thrifty Move</title>
        <meta name="description" content="Find answers to common questions about Thrifty Move Melbourne removalists. Pricing from $130/hr, service areas, booking process, and what's included." />
        <link rel="canonical" href="https://thrifty-move.com.au/faq" />
        <meta property="og:title" content="FAQ | Melbourne Removalists | Thrifty Move" />
        <meta property="og:description" content="Common questions about Melbourne removalist services from $130/hr. Learn about pricing, service areas, and booking." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/faq" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are Thrifty Move's hourly rates?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our rates are: 4-6 Tonne Truck $130/hr, 8-10 Tonne Truck $140/hr, 12+ Tonne Truck from $187/hr. All rates include 2 removalists and GST with a 2-hour minimum booking."
                }
              },
              {
                "@type": "Question",
                "name": "What areas does Thrifty Move service?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We service all Melbourne suburbs with strong coverage in South-East and Eastern Melbourne including Oakleigh, Clayton, Glen Waverley, Hawthorn, and more."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white pt-28 md:pt-32 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-gray-900" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-300">
              Find answers to common questions about our moving services
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    data-testid={`faq-question-${index}`}
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div 
                      className="px-6 pb-5 text-gray-600"
                      data-testid={`faq-answer-${index}`}
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 mb-8">
              Our friendly team is here to help. Get in touch with us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={contact.phoneLink}
                className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-8 py-4 rounded-lg transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call {contact.phone}
              </a>
              <a 
                href={contact.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4 rounded-lg transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
